import { Event } from '@/types/event/event';
import { Price } from '@/types/price/price';

interface UpdateEventData {
    title: string;
    description: string;
    date: string;
    location: string;
    time_start: string;
    time_end: string;
    contact_email: string;
    contact_number: string;
    tickets_available: number;
    bursary_enabled: boolean;
    bursary_type: 'fixed' | 'custom';
    bursary_options: { amount: number; description: string; }[];
    ticket_sales_active: boolean;
    org_uuid: string | null;
    event_organizers: string[];
    stripe_enabled: number;
    e_transfer_enabled: number;
    stripe_product_id: string | null;
    deleted_prices: string[];
    updated_prices: Price[];
}

export const eventService = {
    async getEvent(uuid: string): Promise<Event> {
        const response = await fetch(`${process.env.REACT_APP_API_URL}/events/event/${uuid}`, {
            credentials: 'include',
            headers: {
                'Content-Type': 'application/json'
            }
        });

        if (!response.ok) {
            throw new Error('Failed to fetch event');
        }

        const data = await response.json();
        return {
            ...data,
            tickets_available: data.tickets_available || 0
        };
    },

    async getPricesForEvent(uuid: string): Promise<Price[]> {
        const response = await fetch(`${process.env.REACT_APP_API_URL}/prices/event/${uuid}`, {
            credentials: 'include',
            headers: {
                'Content-Type': 'application/json'
            }
        });

        if (!response.ok) {
            throw new Error('Failed to fetch prices');
        }

        const prices = await response.json();
        return prices.map((price: Price) => ({ ...price, dollar_cost: price.unit_amount / 100 }));
    },

    async getTicketsAvailableForPrice(priceUuid: string): Promise<number> {
        const response = await fetch(`${process.env.REACT_APP_API_URL}/tickets/get-tickets-left-for-price/${priceUuid}`, {
            credentials: 'include',
            headers: {
                'Content-Type': 'application/json'
            }
        });

        if (!response.ok) {
            throw new Error('Failed to fetch tickets available');
        }

        return response.json();
    },

    async updateEvent(uuid: string, data: UpdateEventData): Promise<Event> {
        const response = await fetch(`${process.env.REACT_APP_API_URL}/events/${uuid}`, {
            method: 'PUT',
            credentials: 'include',
            headers: {
                'Content-Type': 'application/json'
            },
            body: JSON.stringify(data)
        });

        if (!response.ok) {
            const contentType = response.headers.get('content-type');
            let errorMessage = 'Failed to update event';
            
            if (contentType && contentType.includes('application/json')) {
                const errorData = await response.json();
                errorMessage = errorData.message || errorData.error || errorMessage;
            } else {
                const text = await response.text();
                errorMessage = text || errorMessage;
            }
            
            throw new Error(errorMessage);
        }

        return response.json();
    },

    async deleteEvent(uuid: string): Promise<void> {
        const response = await fetch(`${process.env.REACT_APP_API_URL}/events/${uuid}`, {
            method: 'DELETE',
            credentials: 'include',
            headers: {
                'Content-Type': 'application/json'
            }
        });

        if (!response.ok) {
            const contentType = response.headers.get('content-type');
            let errorMessage = 'Failed to delete event';
            
            if (contentType && contentType.includes('application/json')) {
                const errorData = await response.json();
                errorMessage = errorData.message || errorData.error || errorMessage;
            } else {
                const text = await response.text();
                errorMessage = text || errorMessage;
            }
            
            throw new Error(errorMessage);
        }
    }
}; 